import { useState, type FormEvent } from "react";
import { ArrowRight, Building2, Check, UserRound } from "lucide-react";
import { accountApi, completePendingConsumerActions } from "../api";
import { AccountShell } from "../components/AccountShell";

function safeNextPath(fallback: string) {
  const next = new URLSearchParams(window.location.search).get("next");
  return next?.startsWith("/") && !next.startsWith("//") ? next : fallback;
}

export function UnifiedLoginPage() {
  const initialType = new URLSearchParams(window.location.search).get("accountType") === "organization"
    ? "organization"
    : "consumer";
  const [accountType, setAccountType] = useState<"consumer" | "organization">(initialType),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const body = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      const result = await accountApi<{ accountType: "consumer" | "organization" }>(
        "/api/account/login",
        { method: "POST", body: JSON.stringify({ ...body, accountType }) },
      );
      if (result.accountType === "consumer") await completePendingConsumerActions();
      window.location.assign(safeNextPath(result.accountType === "organization" ? "/organisaatioille" : "/oma"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Kirjautuminen epäonnistui.");
      setLoading(false);
    }
  };

  return (
    <AccountShell>
      <main className="account-auth">
        <section className="account-auth-copy">
          <span className="eyebrow">Yksi kirjautumissivu</span>
          <h1>Valitse oikea tilityyppi</h1>
          <p>Henkilötili ja organisaatiotili ovat toisistaan erillisiä. Valinta ratkaisee, mihin tietoihin istunto pääsee.</p>
          <ul>
            <li><Check size={17} /> Henkilötilillä omat paikat, luvat ja ostokset</li>
            <li><Check size={17} /> Organisaatiotilillä työtila, luvat ja talous</li>
            <li><Check size={17} /> Tiedot eivät sekoitu tilien välillä</li>
          </ul>
        </section>
        <section className="account-card">
          <div className="account-card-icon">
            {accountType === "consumer" ? <UserRound size={24} /> : <Building2 size={24} />}
          </div>
          <h2>Kirjaudu sisään</h2>
          <div className="account-type-switch" role="tablist" aria-label="Tilityyppi">
            <button type="button" role="tab" aria-selected={accountType === "consumer"} onClick={() => { setAccountType("consumer"); setError(""); }}>
              <UserRound size={16} /> Henkilö
            </button>
            <button type="button" role="tab" aria-selected={accountType === "organization"} onClick={() => { setAccountType("organization"); setError(""); }}>
              <Building2 size={16} /> Organisaatio
            </button>
          </div>
          <form onSubmit={submit}>
            <label>
              {accountType === "consumer" ? "Henkilötilin sähköposti" : "Organisaatiotunnuksen sähköposti"}
              <input name="email" type="email" autoComplete="email" required />
            </label>
            <label>
              Salasana
              <input name="password" type="password" autoComplete="current-password" required />
            </label>
            {error && <p className="account-error">{error}</p>}
            <button disabled={loading}>{loading ? "Odota…" : "Kirjaudu"}<ArrowRight size={17} /></button>
          </form>
          <p>
            {accountType === "consumer"
              ? <>Ei henkilötiliä? <a href="/rekisteroidy">Rekisteröidy käyttäjäksi</a></>
              : <>Ei organisaatiotunnusta? <a href="/organisaatioille/rekisteroidy">Rekisteröi organisaatio</a></>}
          </p>
        </section>
      </main>
    </AccountShell>
  );
}

export function ConsumerRegistrationPage() {
  const [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    const body = Object.fromEntries(new FormData(event.currentTarget).entries());
    try {
      await accountApi("/api/account/register", { method: "POST", body: JSON.stringify(body) });
      await completePendingConsumerActions();
      window.location.assign(safeNextPath("/oma"));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Rekisteröinti epäonnistui.");
      setLoading(false);
    }
  };

  return (
    <AccountShell>
      <main className="account-auth">
        <section className="account-auth-copy">
          <span className="eyebrow">Henkilökohtainen tili</span>
          <h1>Luo oma kalastustili</h1>
          <p>Tämä rekisteröinti on vain henkilökohtaisia kalapaikkoja, lupia ja ostoksia varten.</p>
          <ul>
            <li><Check size={17} /> Tallennetut järvet ja joet</li>
            <li><Check size={17} /> Omat luvat ja ostokset</li>
            <li><Check size={17} /> Henkilökohtainen Core-tarkistus</li>
          </ul>
        </section>
        <section className="account-card">
          <div className="account-card-icon"><UserRound size={24} /></div>
          <h2>Rekisteröidy käyttäjäksi</h2>
          <form onSubmit={submit}>
            <label>Nimi<input name="displayName" autoComplete="name" minLength={2} maxLength={80} required /></label>
            <label>Sähköposti<input name="email" type="email" autoComplete="email" required /></label>
            <label>Salasana<input name="password" type="password" autoComplete="new-password" minLength={12} required /></label>
            {error && <p className="account-error">{error}</p>}
            <button disabled={loading}>{loading ? "Luodaan…" : "Luo henkilötili"}<ArrowRight /></button>
          </form>
          <p>Onko sinulla jo henkilötili? <a href="/kirjaudu?accountType=consumer">Kirjaudu</a></p>
        </section>
      </main>
    </AccountShell>
  );
}
